<template>
  <div
    v-show="!hideAdapter"
    class="flex flex-col rounded-2xl border border-slate-300 px-4 py-2 dark:border-dark-3 dark:bg-dark-2/50"
    :class="{ 'ml-4': isReplacement }"
  >
    <header class="flex justify-end">
      <em
        v-if="showRemove"
        class="mdi mdi-close cursor-pointer text-2xl text-slate-500 dark:text-light-1"
        @click="$emit('remove', adapter)"
      />
    </header>

    <BaseDropdown
      v-model:selection="localAdapter.customizedNumber"
      label="Adapter"
      dropdown-text="Adapter wählen"
      :options-list="adapterTypeList"
      :recent-options-list="recommendedAdaptersPerSession"
      :disabled="disabled"
      @update:selection="onAdapterChanged"
    />

    <div class="relative md:flex md:gap-4">
      <BaseFormInput
        v-model="localAdapter.customizedNumber"
        class="md:basis-1/2"
        :validation="v$?.adapter.customizedNumber"
        :disabled="disabled"
        type="number"
        label="KALO-Artikel"
      >
        <template v-if="!disabled" #trailing>
          <span
            class="mdi mdi-qrcode-scan text-3xl"
            @click="openDeviceCodeScanner"
          />
        </template>
      </BaseFormInput>

      <BaseFormInput
        v-model="localAdapter.productKey"
        class="md:basis-1/2"
        :validation="v$?.adapter.productKey"
        :disabled="disabled"
        label="Lieferant-Artikel"
      />
    </div>

    <div v-if="service">
      <BaseDropdown
        v-model:selection="service.type"
        label="Serviceleistung"
        dropdown-text="Serviceleistung wählen"
        :options-list="adapterServiceTypeList"
        :clearable="true"
        @update:selection="onServiceTypeChange"
      />

      <div v-if="service.type" class="relative md:flex md:gap-4">
        <BaseDropdown
          v-model:selection="service.reason"
          label="Grund auswählen"
          dropdown-text="Serviceleistung wählen"
          :options-list="adapterServiceList"
          class="md:basis-3/4"
          @update:selection="onReasonChanged"
        />
        <BaseFormInput
          v-model="localDate"
          label="Servicedatum"
          placeholder="Datum"
          type="date"
          disabled
        />
      </div>

      <BaseTextarea
        v-if="service.reason === AdapterServiceReasonEnum.OTHER"
        v-model:input-text="service.note"
        :validation="v$?.accessoryService.note"
        test-id="adapter_note"
        label="Notiz"
      />
    </div>
  </div>

  <div v-if="isCodeScanActive">
    <DeviceCodeScanner
      :device-type="AccessoryType.SRT_ADAPTER"
      @scan-result="scanResult($event)"
    />
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue';
import BaseDropdown from '@/components/UI/Dropdown/BaseDropdown.vue';
import BaseFormInput from '@/components/UI/Input/BaseFormInput.vue';
import BaseTextarea from '@/components/UI/Note/BaseTextarea.vue';
import { Adapter } from '@/models/installationPoint/Accessory';
import { AccessoryService } from '@/models/installationPoint/AccessoryService';
import {
  adapterTypeList,
  adapterServiceTypeList,
  resolveAccessoryService,
  AdapterServiceTypeEnum,
  AdapterServiceReasonEnum,
  AccessoryType,
} from '@/enums/device';
import { useInstallationPointStore } from '@/store/entities/installationPointStore';
import { useValidation } from '@/composables/useValidation';
import DeviceCodeScanner from '@/components/UI/Scanner/DeviceCodeScanner.vue';
import { SRTAdapterScanResult } from '@/models/scanner/ScanResult';
import useHardwareScanListener from '@/components/UI/Scanner/useHardwareScanListener';

const props = defineProps<{
  adapter: Adapter;
  service?: AccessoryService;
  showRemove?: boolean;
  isReplacement?: boolean;
  replacementService?: AccessoryService;
}>();

const emit = defineEmits<{
  (e: 'change', adapter: Adapter): void;
  (
    e: 'exchange',
    payload: {
      oldAdapter: Adapter;
      newAdapter: Adapter;
      service?: AccessoryService;
    }
  ): void;
  (e: 'remove', adapter: Adapter): void;
  (
    e: 'serviceTypeChange',
    payload: {
      oldAdapter: Adapter;
      serviceTypeValue?: string;
    }
  ): void;
}>();

const isCodeScanActive = ref(false);
const openDeviceCodeScanner = () => {
  return (isCodeScanActive.value = true);
};

const localAdapter = computed({
  get() {
    return props.adapter;
  },
  set(val: Adapter) {
    emit('change', val);
  },
});

const service = computed(() => props.service);

const localDate = computed({
  get() {
    return service.value?.timestamp?.substring(0, 10);
  },
  set(v: string | undefined) {
    if (service.value) {
      service.value.timestamp = v ?? new Date().toISOString();
    }
  },
});

const recommendedAdaptersPerSession = computed(() => {
  return useInstallationPointStore().recommendedAdaptersPerSession;
});

const adapterServiceList = computed(() => {
  if (!service.value) return [] as any[];
  const { reasonPool } = resolveAccessoryService(service.value.type);
  return reasonPool as any[];
});

const { v$ } = useValidation({
  adapter: localAdapter as any,
  accessoryService: service as any,
});

const hideAdapter = computed(() => {
  if (props.replacementService) {
    return (
      props.replacementService &&
      props.isReplacement &&
      props.replacementService.reason === AdapterServiceReasonEnum.DEFECT
    );
  }

  return false;
});

const disabled = computed(() => {
  return !props.showRemove && !props.isReplacement;
});

function onAdapterChanged() {
  emit('change', localAdapter.value);
}

function onReasonChanged(newValue: any) {
  if (service.value?.type === AdapterServiceTypeEnum.EXCHANGE) {
    const newAdapter = new Adapter({ type: props.adapter.type });
    newAdapter.mountedAt = new Date().toISOString() as any;

    if (newValue === AdapterServiceReasonEnum.DEFECT) {
      newAdapter.customizedNumber = props.adapter.customizedNumber;
      newAdapter.productKey = props.adapter.productKey;
    }

    emit('exchange', {
      oldAdapter: props.adapter,
      newAdapter,
      service: service.value,
    });
  }
}

function onServiceTypeChange(newValue: any) {
  if (
    service.value?.type === AdapterServiceTypeEnum.EXCHANGE &&
    service.value.newAccessoryId
  )
    return;

  emit('serviceTypeChange', {
    oldAdapter: props.adapter,
    serviceTypeValue: newValue,
  });
}

const scanResult = (scanDataObject: SRTAdapterScanResult | object) => {
  if (scanDataObject instanceof SRTAdapterScanResult) {
    localAdapter.value.productKey = scanDataObject.productKey;
    localAdapter.value.customizedNumber = scanDataObject.customizedNumber;
    localAdapter.value.unknownProperties = scanDataObject.unknownProperties;
  }

  isCodeScanActive.value = false;
  return;
};

useHardwareScanListener(scanResult, AccessoryType.SRT_ADAPTER);
</script>
